import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { gql } from '~/__generated__'

const ADD_PLAYLISTS_VIDEO = gql(`
  mutation addPlaylistsVideo($input: AddPlaylistsVideoMutationInput!) {
    addPlaylistsVideo(input: $input) {
      errors
      playlistsVideo {
        id
        playlist {
          id
        }
      }
    }
  }
`)

const NEW_PLAYLISTS_VIDEO_FRAGMENT = gql(`
  fragment NewPlaylistsVideo on PlaylistsVideo {
    id
    playlist {
      id
    }
  }
`)

export const useAddPlaylistVideo = () => {
  const [addPlaylistsVideoMutation, { loading, error }] =
    useMutation(ADD_PLAYLISTS_VIDEO)

  const addPlaylistVideo = useCallback(
    async (playlistId: string, videoId: string) => {
      const result = await addPlaylistsVideoMutation({
        variables: {
          input: { playlistId, videoId },
        },
        optimisticResponse: {
          addPlaylistsVideo: {
            __typename: 'AddPlaylistsVideoMutationPayload',
            errors: [],
            playlistsVideo: {
              __typename: 'PlaylistsVideo',
              id: `temp-add-${playlistId}-${videoId}`,
              playlist: {
                __typename: 'Playlist',
                id: playlistId,
              },
            },
          },
        },
        update: (cache, { data }) => {
          const newEntry =
            data?.addPlaylistsVideo?.playlistsVideo
          if (!newEntry) return

          cache.modify({
            id: `OriginalVideo:${videoId}`,
            fields: {
              playlistsVideos(existing = []) {
                const newRef = cache.writeFragment({
                  data: newEntry,
                  fragment: NEW_PLAYLISTS_VIDEO_FRAGMENT,
                })
                return [...existing, newRef]
              },
            },
          })
        },
      })

      if (
        result.data?.addPlaylistsVideo?.errors &&
        result.data.addPlaylistsVideo.errors.length > 0
      ) {
        throw new Error(
          result.data.addPlaylistsVideo.errors.join(', '),
        )
      }
    },
    [addPlaylistsVideoMutation],
  )

  return { addPlaylistVideo, loading, error }
}
